import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AiOutlineClose } from "react-icons/ai";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { useAppDispatch } from "../../../../app/hook";
import {
  AuthRTKService,
  useDeleteProfileAvatarMutation,
  useGetProfileQuery,
  useUpdateAvatarMutation,
  useUpdateProfileMutation
} from "../../../../app/services/auth-rtk.service";
import { useUploadToCDNMutation } from "../../../../app/services/chatbot-rtk.services";
import Alert, { IAlertType } from "../../../../components/common/Alert";
import avatarPlaceholder from "../../../../components/dashboard/avatar.png";
import { AlertsType } from "../../../../constants/alerts.constant";
import { CountryCode } from "../../../../constants/country-code.constant";
import { ProfileInfoEditType } from "./profile-info-edit.type";

const ProfileInfoEditPage = ({
  showModal,
  setShowModal
}: {
  showModal: boolean;
  setShowModal?: (show: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const schema = Yup.object({
    user_first_name: Yup.string()
      .max(30)
      .required(t(AlertsType.ISPFKO006))
      .label("First name"),
    user_last_name: Yup.string().max(30).nullable().label("Last name"),
    user_user_name: Yup.string().max(30).nullable().label("Username"),
    user_country_code: Yup.string().nullable(),
    user_birth_date: Yup.date()
      .nullable()
      .transform((curr, orig) => (orig === "" ? null : curr))
      .transform((curr, orig) => {
        if (
          orig === "" ||
          orig === "Invalid date" ||
          !moment(orig, "YYYY-MM-DD", true).isValid()
        ) {
          return null;
        }
        return curr;
      })
      .test(
        "not-in-future",
        t("ProfileInfoEditPage/birthday-error-future-date"),
        function (value) {
          if (!value) return true;
          return moment(value).isSameOrBefore(moment(), "day");
        }
      )
      .test(
        "age",
        t("ProfileInfoEditPage/birthday-error-too-young"),
        function (value) {
          if (!value) return true;
          const cutoff = moment().subtract(14, "years");
          return moment(value).isSameOrBefore(cutoff);
        }
      )
  });

  const profile = useGetProfileQuery();
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();
  const [updateAvatar, updateAvatarResults] = useUpdateAvatarMutation();
  const [deleteAvatar, deleteAvatarResults] = useDeleteProfileAvatarMutation();
  const [cdnUploader, cdnUploaderResults] = useUploadToCDNMutation();

  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const [alert, setAlert] = useState<IAlertType>({
    isShow: false,
    title: "",
    type: "warning",
    message: ""
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<ProfileInfoEditType>({
    resolver: yupResolver(schema) as any,
    mode: "onSubmit"
  });

  const onSubmit = (data: any) => {
    closeAlert();

    let formattedBirthDate = data.user_birth_date || null;

    if (data.user_birth_date) {
      const parsedDate = moment(data.user_birth_date, "YYYY-MM-DD", true);
      formattedBirthDate = parsedDate.isValid()
        ? parsedDate.format("YYYY-MM-DD")
        : null;
    } else {
      formattedBirthDate = null;
    }

    updateProfile({
      new_user_birth_date: formattedBirthDate,
      new_user_country_code: data.user_country_code,
      new_user_first_name: data.user_first_name,
      new_user_last_name: data.user_last_name,
      new_user_user_name: data.user_user_name
    })
      .unwrap()
      .then(() => {
        setAlert({
          isShow: true,
          message: t(AlertsType.ISPFKO011),
          type: "success",
          title: ""
        });
      })
      .catch((res) => {
        if (res.status == 400 && res.data && res.data.element) {
          let message = "";

          Object.values(res.data.element).forEach((item) => {
            message += `${item} \n`;
          });

          setAlert({
            ...alert,
            isShow: true,
            message: message,
            type: "warning"
          });
        }
      });
  };

  const closeAlert = () => {
    setAlert({
      isShow: false,
      title: "",
      type: "warning",
      message: ""
    });
  };

  useEffect(() => {
    if (profile?.isSuccess && profile?.data) {
      setValue("user_first_name", profile?.data?.user_first_name);
      setValue("user_last_name", profile?.data?.user_last_name);
      setValue("user_country_code", profile?.data?.user_country_code);
      setValue(
        "user_birth_date",
        profile?.data?.user_birth_date
          ? moment(profile?.data?.user_birth_date).format("YYYY-MM-DD")
          : ""
      );
      setValue("user_user_name", profile?.data?.user_user_name);
    }
  }, [profile?.isSuccess]);

  const close = () => {
    if (profile?.isSuccess && profile?.data) {
      setValue("user_first_name", profile?.data?.user_first_name);
      setValue("user_last_name", profile?.data?.user_last_name);
      setValue("user_country_code", profile?.data?.user_country_code);
      setValue(
        "user_birth_date",
        moment(profile?.data?.user_birth_date).format("YYYY-MM-DD")
      );
      setValue("user_user_name", profile?.data?.user_user_name);
    }
    setShowModal?.(false);
  };

  const handleTriggerFileSelection = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
      inputFileRef.current.click();
    }
  };

  const handleUploadFileToCdn = (files: FileList | null) => {
    if (files && files.length > 0) {
      const formData = new FormData();

      if (files[0].size > 5 * 1024 * 1024) {
        toast.error(t(AlertsType.ISSTKO008, { size: 5 }));
        return false;
      }

      formData.append(
        "files",
        files[0],
        profile.data?.user_identification + "_" + files[0].name
      );

      cdnUploader(formData)
        .unwrap()
        .then((value) => {
          updateAvatar({ avatar: value[0].cdnPath })
            .unwrap()
            .then(() => {
              dispatch(AuthRTKService.util.invalidateTags(["Profile"]));
            })
            .catch((err) => {
              throw err;
            });
        })
        .catch((err) => {
          toast.error(err.data.message || err.message);
        })
        .finally(() => {
          if (inputFileRef.current) inputFileRef.current.value = "";
        });
    }
  };

  const handleDeleteAvatar = () => {
    deleteAvatar()
      .unwrap()
      .then(() => {
        dispatch(AuthRTKService.util.invalidateTags(["Profile"]));
      })
      .catch((err) => {
        toast.error(err.data.message || err.message);
      });
  };

  return (
    <>
      {showModal && (
        <Modal show={showModal} centered onHide={() => close()} size="lg">
          <Modal.Body className="tw-p-5">
            <Row className="m-0">
              <Col className="tw-flex tw-justify-between tw-items-start tw-px-0">
                <div>
                  <h4 className="m-0 mt-2 font-sofiaSans tw-font-bold tw-text-4xl ">
                    {t("ProfileEditPage/l-07")}
                  </h4>
                </div>
                <AiOutlineClose
                  size={24}
                  className="tw-cursor-pointer tw-text-neutral-400"
                  onClick={() => close()}
                />
              </Col>
            </Row>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="pt-lg-3 dashboard-profile-edit-account-content">
                <Col>
                  <Row>
                    <Col>
                      {alert.isShow && (
                        <Alert type={alert.type} onClick={() => closeAlert()}>
                          {alert.message
                            .split("\n")
                            .filter((item) => item !== "")
                            .map((item, index) => (
                              <p className="tw-mb-1" key={index}>
                                {item}
                              </p>
                            ))}
                        </Alert>
                      )}
                    </Col>
                  </Row>

                  <div className="tw-h-[72px] tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
                    <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                      <div className="tw-self-stretch tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                        Avatar
                      </div>
                      <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-5 tw-inline-flex">
                        <img
                          className="tw-w-12 tw-h-12 tw-rounded-[99px] tw-object-cover"
                          src={profile.data?.user_avatar || avatarPlaceholder}
                          alt="avatar"
                        />
                        <div className="tw-justify-start tw-items-center tw-gap-3 tw-flex">
                          {}
                          <Button
                            onClick={handleTriggerFileSelection}
                            variant=""
                            color=""
                            className="tw-text-gray-700 text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-px-[13px] tw-py-[9px] tw-bg-white tw-rounded-md tw-shadow border tw-border-gray-300 tw-justify-center tw-items-center tw-flex"
                            disabled={
                              cdnUploaderResults.isLoading ||
                              updateAvatarResults.isLoading ||
                              deleteAvatarResults.isLoading
                            }
                          >
                            {(cdnUploaderResults.isLoading ||
                              updateAvatarResults.isLoading) && (
                              <Spinner size="sm" className="tw-mr-2" />
                            )}

                            {!profile.data?.user_avatar
                              ? t("ProfileInfoEditPage/avatar-upload")
                              : t("ProfileInfoEditPage/avatar-change")}
                          </Button>
                          {profile.data?.user_avatar && (
                            <Button
                              variant=""
                              color=""
                              className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-px-[13px] tw-py-[9px] tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-flex"
                              onClick={handleDeleteAvatar}
                              disabled={deleteAvatarResults.isLoading}
                            >
                              {deleteAvatarResults.isLoading && (
                                <Spinner size="sm" className="tw-mr-2" />
                              )}
                              {t("ProfileInfoEditPage/avatar-delete")}
                            </Button>
                          )}
                        </div>

                        <input
                          type="file"
                          hidden
                          ref={inputFileRef}
                          accept="image/*"
                          onChange={(e) =>
                            handleUploadFileToCdn(e.target.files)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <Row className="mt-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>{t("ProfileInfoEditPage/l-01")}</Form.Label>
                        <Form.Control {...register("user_first_name")} />
                        <Form.Control.Feedback
                          type="invalid"
                          className="d-block font-poppins"
                        >
                          {errors.user_first_name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>{t("ProfileInfoEditPage/l-02")}</Form.Label>
                        <Form.Control {...register("user_last_name")} />
                        <Form.Control.Feedback
                          type="invalid"
                          className="d-block font-poppins"
                        >
                          {errors.user_last_name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>{t("ProfileInfoEditPage/l-03")}</Form.Label>
                        <Form.Control {...register("user_user_name")} />
                        <Form.Control.Feedback
                          type="invalid"
                          className="d-block font-poppins"
                        >
                          {errors.user_user_name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>{t("ProfileInfoEditPage/l-04")}</Form.Label>
                        <Form.Control
                          type="date"
                          placeholder="DD/MM/YYYY"
                          {...register("user_birth_date")}
                        />
                        <Form.Control.Feedback
                          type="invalid"
                          className="d-block font-poppins"
                        >
                          {errors.user_birth_date?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col
                      md={2}
                      className="d-flex align-items-center justify-content-center"
                    >
                      {profile.data?.user_country_code && (
                        <div className="d-flex justify-content-center justify-content-md-start">
                          <img
                            className="tw-h-[60px] tw-max-w-[60px]"
                            src={`https://cdn.1cdn.app/application/LOUISEBOT/${profile.data?.user_country_code.toLowerCase()}.svg`}
                            alt={`logo_${profile.data?.user_country_code}.svg`}
                          />
                        </div>
                      )}
                    </Col>
                    <Col md={10} className="align-items-end">
                      <Form.Group>
                        <Form.Label>{t("ProfileInfoEditPage/l-05")}</Form.Label>
                        <Form.Select
                          {...register("user_country_code")}
                          className="color-fifthColor"
                        >
                          {CountryCode.map((item) => (
                            <option
                              className="color-fifthColor"
                              key={item[1]}
                              value={item[1]}
                            >
                              {item[0]}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback
                          type="invalid"
                          className="d-block font-poppins"
                        >
                          {errors.user_country_code?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="dashboard-profile-edit-account-footer">
                <Col className="pt-4 d-flex justify-content-start ">
                  <Button
                    variant="primary"
                    size="lg"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner animation="grow" />
                    ) : (
                      t("ProfileInfoEditPage/l-06")
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default ProfileInfoEditPage;
